import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiFetch } from "@/api/client"

import {
  ADMIN_QUERY_KEYS,
  type ActiveSyncJobsResponse,
  type SyncStatusResponse,
  type SyncTriggerResponse,
} from "./adminTypes"

// ---------------------------------------------------------------------------
// Sync
// ---------------------------------------------------------------------------

export function useActiveSyncJobs(enabled: boolean) {
  return useQuery<ActiveSyncJobsResponse>({
    queryKey: ADMIN_QUERY_KEYS.syncActive,
    queryFn: () =>
      apiFetch<ActiveSyncJobsResponse>("/api/sync/active"),
    refetchInterval: enabled ? 3_000 : false,
    refetchIntervalInBackground: false,
    retry: false,
    enabled,
  })
}

export function useSyncStatus(jobId: string | null) {
  return useQuery<SyncStatusResponse>({
    queryKey: ADMIN_QUERY_KEYS.syncStatus(jobId ?? ""),
    queryFn: () =>
      apiFetch<SyncStatusResponse>(`/api/sync/status/${jobId}`),
    refetchInterval: jobId ? 5_000 : false,
    refetchIntervalInBackground: false,
    enabled: jobId !== null,
    retry: false,
  })
}

export function useTriggerSync() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      startDate: string
      endDate: string
      dataSources: string[]
      accountIds?: string[]
    }) =>
      apiFetch<SyncTriggerResponse>("/api/sync/unified", {
        method: "POST",
        body: data,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.syncActive })
      qc.invalidateQueries({ queryKey: ["sync", "logs"] })
    },
  })
}

export function useTriggerKeywordsSync() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { startDate: string; endDate: string }) =>
      apiFetch<SyncTriggerResponse>("/api/sync/adw-keywords", {
        method: "POST",
        body: data,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.syncActive })
      qc.invalidateQueries({ queryKey: ["sync", "logs"] })
    },
  })
}

export function useCancelSyncJob() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (logId: number) =>
      apiFetch<{ success: boolean; message: string }>(
        `/api/sync/cancel/${logId}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.syncActive })
      qc.invalidateQueries({ queryKey: ["sync", "logs"] })
    },
  })
}

export function useLastSyncInfo() {
  return useQuery({
    queryKey: ADMIN_QUERY_KEYS.lastSync,
    queryFn: () =>
      apiFetch<{ success: boolean; data: { started_at: string | null; status: string }[] }>(
        "/api/sync/logs?limit=1",
      ),
    retry: false,
  })
}
